import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginService } from '@core/authentication';
import { AuthWindowComponent } from '../auth-window/auth-window.component';

const MIN_LENGTH = 8;

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const { password, confirmation } = group.value as { password: string; confirmation: string };
  return confirmation && password !== confirmation ? { mismatch: true } : null;
}

/** New password from the link of the reset mail (`/auth/reset-password/:token`). */
@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, RouterLink, MatIcon, MatTooltip, TranslateModule, AuthWindowComponent],
})
export class ResetPasswordComponent {
  private readonly fb = inject(FormBuilder);
  private readonly loginService = inject(LoginService);
  private readonly translate = inject(TranslateService);
  private readonly token: string = inject(ActivatedRoute).snapshot.params.token ?? '';

  readonly minLength = MIN_LENGTH;
  readonly submitting = signal(false);
  readonly showPassword = signal(false);
  readonly error = signal<string | null>(null);
  readonly done = signal(false);

  readonly form = this.fb.nonNullable.group(
    {
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(MIN_LENGTH)]],
      confirmation: ['', [Validators.required]],
    },
    { validators: passwordsMatch }
  );

  invalid(name: 'email' | 'password' | 'confirmation'): boolean {
    const control = this.form.controls[name];
    const mismatch = name === 'confirmation' && this.form.hasError('mismatch');
    return (control.invalid || mismatch) && control.touched;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { email, password } = this.form.getRawValue();
    this.submitting.set(true);
    this.error.set(null);
    this.loginService.resetPassword(email.trim(), password, this.token).subscribe({
      next: () => {
        this.submitting.set(false);
        this.done.set(true);
      },
      error: (response: HttpErrorResponse) => {
        this.submitting.set(false);
        // Invalid or expired link, too short password: the backend sends the reason.
        this.error.set(response.error?.message ?? this.translate.instant('auth.error_generic'));
      },
    });
  }
}
